import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LadderGameModal from './LadderGameModal';
import BottleSpinModal, { BottleParticipant } from './BottleSpinModal';
import ParticipantSourceSheet, { ParticipantItem } from './ParticipantSourceSheet';
import { LadderIcon } from './LadderIcons';
import { SectionHeader } from 'components/ds';
import styles from './MiniGamesSection.module.css';

/** 홈 배너에서 "게임하러 가기" 를 눌렀을 때 스크롤할 자리.
 *  클래스명은 CSS Modules 가 해시하므로 id 로 고정합니다. */
export const MINI_GAMES_ANCHOR_ID = 'mini-games';

export interface MiniGamesSectionProps {
    /** 모임에 참여자가 있으면 자동 로드 (홈에선 생략) */
    initialParticipants?: BottleParticipant[];
    /** 제목 유무 (홈=표시, 모임상세=카드 헤더로 대체) */
    showTitle?: boolean;
    /** 컴팩트 모드 (모임상세 카드 안에 들어갈 때) */
    compact?: boolean;
}

const BottleIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
    // 게임마다 다른 색을 칠하면 색이 뜻을 갖지 않습니다. 사다리 아이콘과 같은 선으로 그립니다.
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M17 4h6v4h-6z" />
        <path d="M16 8h8v10" />
        <path d="M16 18q0 4-6 8v10h20V26q-6-4-6-8" />
        <path d="M12 29h16" />
    </svg>
);

const MiniGamesSection: React.FC<MiniGamesSectionProps> = ({ initialParticipants, showTitle = true, compact = false }) => {
    const { t } = useTranslation();
    const [showLadder, setShowLadder] = useState(false);
    const [showBottle, setShowBottle] = useState(false);
    // 메인탭에서 참여자 직접 지정이 필요한 경우 쓰는 picker
    const [pickerFor, setPickerFor] = useState<null | 'bottle' | 'ladder'>(null);
    const [pickedParticipants, setPickedParticipants] = useState<ParticipantItem[] | null>(null);

    const hasContextParticipants = !!(initialParticipants && initialParticipants.length > 0);

    const openGame = (game: 'bottle' | 'ladder') => {
        if (hasContextParticipants) {
            // 모임 상세: 컨텍스트 참여자로 바로 실행
            if (game === 'bottle') setShowBottle(true);
            else setShowLadder(true);
        } else {
            // 메인탭: 참여자 소스를 먼저 선택
            setPickedParticipants(null);
            setPickerFor(game);
        }
    };

    const onPickerPick = (list: ParticipantItem[]) => {
        const game = pickerFor;
        setPickedParticipants(list);
        setPickerFor(null);
        if (game === 'bottle') setShowBottle(true);
        else if (game === 'ladder') setShowLadder(true);
    };

    const activeParticipants = hasContextParticipants
        ? (initialParticipants as BottleParticipant[])
        : (pickedParticipants || []);

    return (
        <>
            {showTitle && <SectionHeader title={t('meeting.miniGameTitle')} />}
            <ul id={MINI_GAMES_ANCHOR_ID} className={compact ? `${styles.grid} ${styles.gridCompact}` : styles.grid}>
                <li>
                    <button type="button" className={styles.card} onClick={() => openGame('bottle')}>
                        <span className={styles.cardIcon}><BottleIcon size={40} /></span>
                        <span className={styles.cardBody}>
                            <span className={styles.cardTitle}>{t('games.bottleTitle')}</span>
                            <span className={styles.cardSub}>{t('games.pickPenalty')}</span>
                        </span>
                    </button>
                </li>
                <li>
                    <button type="button" className={styles.card} onClick={() => openGame('ladder')}>
                        <span className={styles.cardIcon}><LadderIcon size={36} color="currentColor" /></span>
                        <span className={styles.cardBody}>
                            <span className={styles.cardTitle}>{t('games.ladderTitle')}</span>
                            <span className={styles.cardSub}>{t('games.leadOrder')}</span>
                        </span>
                    </button>
                </li>
            </ul>

            <ParticipantSourceSheet
                open={pickerFor !== null}
                onClose={() => setPickerFor(null)}
                onPick={onPickerPick}
            />

            <LadderGameModal
                open={showLadder}
                participants={activeParticipants}
                onClose={() => setShowLadder(false)}
            />
            <BottleSpinModal
                open={showBottle}
                initialParticipants={activeParticipants}
                onClose={() => setShowBottle(false)}
            />
        </>
    );
};

export default MiniGamesSection;
